import { FC, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import { useUserSettings } from "../../context/userSettingsContext";
import { createLoadOptions } from "../../hooks/useDebounce";
import { useI18n } from "../../hooks/useTranslations";
import { useAdminCarsApi } from "../../http/cars/api";
import { CarNoCarDepency } from "../../http/cars/types";
import { Maintenance } from "../../http/maintenances/types";
import { useMaintenanceTypesApi } from "../../http/maintenanceTypes/api";
import { MaintenanceType } from "../../http/maintenanceTypes/types";
import { convertKilometersToMiles } from "../../types/utils";
import DatePicker from "../../ui/DatePicker";
import Form from "../ui/Form";
import FormField from "../ui/FormField";
import FormFieldsContainer from "../ui/FormFieldsContainer";
import Numeric from "../ui/Numeric";
import reactSelectStyles from "../ui/ReactSelectUtils";
import Textarea from "../ui/Textarea";

const CAR_FIELDS = ["id", "vehicleIdentificationNumber", "currentPlateNumber", "lastPlateNumber"] as const;
type CarField = (typeof CAR_FIELDS)[number];
type CarSearch = Pick<CarNoCarDepency, CarField>;

const MAINTENANCE_TYPES_FIELDS = ["id", "name"] as const;
type MaintenanceTypeField = (typeof MAINTENANCE_TYPES_FIELDS)[number];
type MaintenanceTypeSearch = Pick<MaintenanceType, MaintenanceTypeField>;

export interface MaintenanceFormBody {
  car: {
    id: string;
    vehicleIdentificationNumber: string;
    currentPlateNumber?: string;
    lastPlateNumber?: string;
  } | null;
  date: string;
  type: MaintenanceType;
  description?: string;
  distance?: number;
  cost?: number;
}

type MaintenanceFormProps = {
  maintenance?: Maintenance | null;
  onSubmit: (form: MaintenanceFormBody) => Promise<void>;
  isDisabled?: boolean;
  isLoading?: boolean;
  allowSelectCar?: boolean;
};

const MaintenanceForm: FC<MaintenanceFormProps> = ({
  maintenance,
  onSubmit,
  isDisabled,
  isLoading,
  allowSelectCar,
}) => {
  const i18n = useI18n();
  const userSettings = useUserSettings();
  const carsApi = useAdminCarsApi();
  const maintenanceTypesApi = useMaintenanceTypesApi();
  const aborter = useRef(new AbortController());
  const [defaultCarOptions, setDefaultCarOptions] = useState<CarSearch[]>([]);
  const distanceInMiles = !!userSettings.me.distanceInMiles;

  const types = maintenanceTypesApi.useAdminMaintenanceTypes(
    { limit: 100, fields: MAINTENANCE_TYPES_FIELDS },
    aborter.current.signal,
  );

  const defaultValues = {
    car: maintenance?.car,
    date: maintenance?.date,
    type: maintenance?.type,
    description: maintenance?.description,
    distance:
      maintenance?.distance != undefined && distanceInMiles
        ? convertKilometersToMiles(maintenance.distance)
        : maintenance?.distance,
    cost: maintenance?.cost,
  };

  return (
    <Form<MaintenanceFormBody>
      defaultValues={defaultValues}
      submitText={i18n.t.common.save}
      disabled={isDisabled}
      onSubmit={() => onSubmit}
    >
      {({ control, register, reset, setValue, watch, formState: { errors } }) => {
        useEffect(() => {
          reset(defaultValues);
        }, [maintenance]);

        return (
          <FormFieldsContainer>
            {!!allowSelectCar && (
              <Controller
                name="car"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <FormField label={i18n.t.cars.singular} error={fieldState.error}>
                    {({ labelId, isInvalid }) => {
                      const searchTerm = useRef("");
                      const aborter = useRef(new AbortController());

                      const loadOptions = createLoadOptions<CarSearch, CarField>(aborter, searchTerm, carsApi.getCars, {
                        fields: CAR_FIELDS,
                      });

                      useEffect(() => {
                        loadOptions(searchTerm.current).then(setDefaultCarOptions);
                      }, []);

                      return (
                        <AsyncSelect
                          menuPosition="fixed"
                          {...field}
                          isDisabled={!!maintenance || isDisabled || isLoading}
                          isLoading={isLoading}
                          inputId={labelId}
                          defaultOptions={defaultCarOptions}
                          loadOptions={loadOptions}
                          placeholder={`${i18n.t.common.select}...`}
                          getOptionLabel={(x) =>
                            x.currentPlateNumber || x.lastPlateNumber || x.vehicleIdentificationNumber
                          }
                          getOptionValue={(x) => x.id}
                          styles={reactSelectStyles<CarSearch>(isInvalid)}
                        />
                      );
                    }}
                  </FormField>
                )}
              />
            )}

            <Controller
              name="date"
              control={control}
              rules={{
                required: true,
                validate: (value) => {
                  const d = new Date(value!);
                  if (d.getFullYear() < 1000) {
                    return i18n.t.common.invalidDate;
                  }
                  return undefined;
                },
              }}
              render={({ fieldState }) => (
                <FormField label={i18n.t.common.date} error={fieldState.error}>
                  {({ isInvalid }) => (
                    <DatePicker
                      onChange={(v) => !!v && setValue("date", v)}
                      value={watch("date")}
                      disabled={isDisabled || isLoading}
                      isInvalid={isInvalid}
                    />
                  )}
                </FormField>
              )}
            />

            <Controller
              name="type"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.t.maintenanceTypes.singular} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Select
                      menuPosition="fixed"
                      {...field}
                      isLoading={isLoading || types.isLoading}
                      isDisabled={isLoading || isDisabled}
                      inputId={labelId}
                      options={types.data?.items}
                      placeholder={`${i18n.t.common.select}...`}
                      getOptionLabel={(x) => x.name}
                      getOptionValue={(x) => x.id}
                      styles={reactSelectStyles<MaintenanceTypeSearch>(isInvalid)}
                    />
                  )}
                </FormField>
              )}
            />

            <FormField label={i18n.t.common.description} error={errors.description} isOptional>
              {({ labelId, isInvalid }) => (
                <Textarea
                  id={labelId}
                  isInvalid={isInvalid}
                  disabled={isDisabled || isLoading}
                  rows={4}
                  {...register("description")}
                />
              )}
            </FormField>

            <Controller
              name="distance"
              control={control}
              render={({ field, fieldState }) => (
                <FormField label={i18n.t.common.distance} error={fieldState.error} isOptional>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      disabled={isLoading || isDisabled}
                      onBlur={(value) => setValue("distance", value ?? undefined)}
                      allowNegative={false}
                      decimalScale={0}
                      suffix={` ${distanceInMiles ? i18n.t.common.miles : "km"}`}
                      isInvalid={isInvalid}
                    />
                  )}
                </FormField>
              )}
            />

            <Controller
              name="cost"
              control={control}
              render={({ field, fieldState }) => (
                <FormField label={i18n.t.common.cost} error={fieldState.error} isOptional>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      disabled={isLoading || isDisabled}
                      onBlur={(value) => setValue("cost", value ?? undefined)}
                      decimalScale={2}
                      suffix=" €"
                      allowNegative={false}
                      isInvalid={isInvalid}
                    />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default MaintenanceForm;
